<template>
  <layout-workspace>
    <template v-slot:header>
      <router-link class="inline-block h-full" to="/">
        <img
          src="@/assets/logos/logo-kbsim.svg"
          alt="Logo"
          class="mx-auto my-4 inline-block"
          style="height: 5vh;opacity: 0.3;"
      /></router-link>
    </template>
    <template v-slot:tabs v-if="tabs.length > 0">
      <button
        :key="tab.id"
        v-for="tab in tabs"
        v-html="tab.name"
        :class="{ selected: tab.id === activeTab }"
        @click="onTabSelected(tab)"
      ></button>
    </template>
    <template v-slot:default>
      <div class="flex flex-row">
        <template v-if="mode === 'battle'">
          <div class="area-fleets flex flex-row">
            <FleetEditor
              v-if="fleet != null"
              :label="$t('views.kbsim.attacker.name')"
              :default-name="$t('views.kbsim.attacker.name')"
              :fleet="fleet"
              :role="'attacker'"
              :drag-bus="dragBus"
              :simulation-bus="simulationBus"
            ></FleetEditor>
            <FleetEditor
              v-if="entity != null"
              :label="$t('views.kbsim.defender.name')"
              :default-name="$t('views.kbsim.defender.name')"
              :fleet="entity"
              :role="'defender'"
              :drag-bus="dragBus"
              :simulation-bus="simulationBus"
            ></FleetEditor>
          </div>
          <battle-report
            v-if="report != null"
            :report="report"
            class="flex-1"
          ></battle-report>
        </template>
        <template v-else-if="mode === 'debris'">
          <div class="area-fleets flex flex-row">
            <debris-gatherer-editor
              v-if="debrisGatherers != null"
              :label="$t('views.kbsim.debris_gatherers.name')"
              :fleet="debrisGatherers"
              :drag-bus="dragBus"
              :simulation-bus="debrisGathererBus"
            ></debris-gatherer-editor>
            <debris-field-editor
              v-if="entity != null"
              :label="$t('views.kbsim.debris_field.name')"
              :fleet="entity"
              :drag-bus="dragBus"
              :simulation-bus="debrisGathererBus"
            >
            </debris-field-editor>
          </div>
          <debris-gatherer-report
            v-if="report != null"
            :report="report"
            class="flex-1"
          ></debris-gatherer-report>
        </template>
      </div>
      <draggable-item-cursor :drag-bus="dragBus">
        <template v-slot:default="content">
          <fleet-item
            v-if="content.entry != null"
            :entry="content.entry"
            :opaque="true"
          ></fleet-item>
        </template>
      </draggable-item-cursor>
    </template>
    <template v-slot:footer>
      <disclaimer />
    </template>
  </layout-workspace>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Disclaimer from "@/components/misc/Disclaimer";
import FleetEditor from "@/components/fleet/FleetEditor";
import BattleReport from "@/components/report/BattleReport";
import LayoutWorkspace from "@/components/layouts/LayoutWorkspace";
import DraggableItemCursor from "@/components/draggable/DraggableItemCursor";
import FleetItem from "@/components/fleet/FleetItem";
import Vue from "vue";
import DebrisGathererEditor from "@/components/fleet/DebrisGathererEditor";
import DebrisGathererReport from "@/components/report/DebrisGathererReport";
import DebrisFieldEditor from "@/components/fleet/DebrisFieldEditor";
import store from "@/store";

export default {
  name: "KbSim",
  components: {
    DebrisFieldEditor,
    DebrisGathererReport,
    DebrisGathererEditor,
    FleetItem,
    DraggableItemCursor,
    LayoutWorkspace,
    BattleReport,
    FleetEditor,
    Disclaimer
  },
  props: {
    launchAction: {
      type: String,
      default: null
    }
  },
  data: () => {
    return {
      dragBus: new Vue(),
      simulationBus: new Vue(),
      debrisGathererBus: new Vue(),
      runUpdate: false,
      activeTab: 1,
      mode: "battle",
      entity: null,
      fleet: null,
      debrisGatherers: null,
      report: null
    };
  },
  computed: {
    ...mapGetters("data", ["round"]),
    ...mapGetters("combat", {
      getEntity: "entity",
      getFleet: "fleet",
      getReport: "report",
      fleetsChanged: "fleetsChanged"
    }),
    ...mapGetters("debris", {
      debrisGathererFleet: "debrisGatherers",
      debrisField: "entity",
      debrisGathererReport: "report",
      debrisGatherersChanged: "fleetChanged"
    }),
    tabs() {
      return [
        {
          id: 1,
          name: this.$t("views.kbsim.tab.wave", { n: 1 }),
          battleIndex: 0,
          mode: "battle"
        },
        {
          id: 2,
          name: this.$t("views.kbsim.tab.wave", { n: 2 }),
          battleIndex: 1,
          mode: "battle"
        },
        {
          id: 3,
          name: this.$t("views.kbsim.tab.debris"),
          mode: "debris"
        }
      ];
    }
  },
  async beforeRouteEnter(to, from, next) {
    const round = to.params.round;
    if (round == null || round.length === 0) {
      next({ name: "Home" });
      return;
    }
    store.commit("data/setRound", round);
    await store.dispatch("data/fetchData");
    next();
  },
  mounted() {
    this.runUpdate = true;
    this.onTabSelected(this.tabs[0]);
    this.updateState();
  },
  destroyed() {
    this.runUpdate = false;
  },
  methods: {
    ...mapActions("combat", ["simulateBattle", "markFleetsChanged"]),
    ...mapActions("debris", {
      calculateDebrisGathererAmounts: "calculateShipAmounts",
      markDebrisGatherersChanged: "markFleetChanged"
    }),
    onTabSelected(tab) {
      this.activeTab = tab.id;
      switch (tab.mode) {
        case "battle": {
          this.entity = this.getEntity(tab.battleIndex);
          this.fleet = this.getFleet(tab.battleIndex);
          this.report = this.getReport(tab.battleIndex);
          break;
        }
        case "debris": {
          this.debrisGatherers = this.debrisGathererFleet;
          this.entity = this.debrisField;
          this.report = this.debrisGathererReport;
        }
      }
      this.mode = tab.mode;
      this.markFleetsChanged();
    },
    updateState() {
      if (!this.runUpdate) {
        return;
      }
      if (this.fleetsChanged) {
        this.simulateBattle();
        this.markDebrisGatherersChanged();
        setTimeout(() => {
          this.simulationBus.$emit("simulate");
        }, 5);
      }
      if (this.debrisGatherersChanged) {
        this.calculateDebrisGathererAmounts();
        setTimeout(() => {
          this.debrisGathererBus.$emit("simulate");
        }, 5);
      }
      setTimeout(() => this.updateState(), 100);
    }
  }
};
</script>

<style scoped>
.area-fleets > * {
  margin-right: 2em;
}
</style>
